// web/src/pages/admin/ContentSources.tsx

import { useState, useCallback } from 'react';
import { Database, X } from 'react-feather';
import PipelineControl from '../../components/admin/PipelineControl';
import JobList from '../../components/admin/JobList';
import LogStream from '../../components/admin/LogStream';
import { PipelineJob, PipelineStatusResponse } from '../../types/pipeline';

export default function ContentSources() {
  const [selectedJob, setSelectedJob] = useState<PipelineJob | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [environment, setEnvironment] = useState<string>('dev');

  const handleJobStarted = useCallback((job: PipelineJob) => {
    setSelectedJob(job);
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleSelectJob = useCallback((job: PipelineJob) => {
    setSelectedJob(job);
  }, []);

  const handleCloseLogStream = useCallback(() => {
    setSelectedJob(null);
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleLogStreamComplete = useCallback(() => {
    // Refresh the job list when a job completes
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleStatusChange = useCallback((status: PipelineStatusResponse['data']) => {
    if (status.environment) {
      setEnvironment(status.environment);
    }
  }, []);

  const handleJobCancelled = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="text-blue-600" size={28} />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Content Sources</h1>
            <p className="text-sm text-gray-500">
              Manage content pipeline execution for RAG knowledge base
            </p>
          </div>
        </div>
      </div>

      {/* Pipeline Control */}
      <PipelineControl onJobStarted={handleJobStarted} onStatusChange={handleStatusChange} />

      {/* Job History */}
      <JobList
        onSelectJob={handleSelectJob}
        refreshTrigger={refreshTrigger}
        environment={environment}
        onJobCancelled={handleJobCancelled}
      />

      {/* Log Stream Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Pipeline Job: {selectedJob.id.substring(0, 8)}...
                </h2>
                <p className="text-sm text-gray-500">
                  Status: {selectedJob.status} | Environment: {selectedJob.environment}
                </p>
              </div>
              <button
                onClick={handleCloseLogStream}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Log Stream */}
            <div className="flex-1 overflow-hidden p-4">
              <LogStream
                jobId={selectedJob.id}
                startedAt={selectedJob.startedAt}
                onComplete={handleLogStreamComplete}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
